package com.evoapps.evoshift.navigation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.navigation.NavController
import kotlinx.browser.window
import org.w3c.dom.PopStateEvent
import org.w3c.dom.events.Event

/**
 * One browser history entry per screen above the first (numbered in the entry's state), pushed
 * and dropped as the back stack grows and shrinks. The browser's back pops the app's back stack;
 * forward is undone (a popped screen is gone). The address never changes.
 *
 * SH-105: an open detail pane (DetailPanes) is an entry too, above its screen's: the browser's
 * back closes the pane first, as the system back does.
 */
@Composable
actual fun BindBrowserBack(navController: NavController) {
    val backStack by navController.currentBackStack.collectAsState()
    val panes by DetailPanes.open.collectAsState()
    val depth = (backStack.routeClasses().size - 1).coerceAtLeast(0) + panes
    val history = remember(navController) { BrowserHistorySync(navController) }
    LaunchedEffect(history, depth) { history.follow(depth) }
    DisposableEffect(history) {
        val listener: (Event) -> Unit = { event -> history.onPopState((event as? PopStateEvent)?.state) }
        window.addEventListener("popstate", listener)
        onDispose { window.removeEventListener("popstate", listener) }
    }
}

private class BrowserHistorySync(private val navController: NavController) {
    /** Entries the app pushed above the page's own. */
    private var depth = 0

    /** Pops the app caused itself (history.go), not the person. */
    private var ownPops = 0

    fun follow(target: Int) {
        when {
            target > depth -> while (depth < target) {
                depth++
                window.history.pushState(depth, "")
            }
            target < depth -> {
                ownPops++
                window.history.go(target - depth)
                depth = target
            }
        }
    }

    fun onPopState(state: Any?) {
        if (ownPops > 0) {
            ownPops--
            return
        }
        val index = (state as? Number)?.toInt() ?: 0
        when {
            index < depth -> {
                val pops = depth - index
                depth = index
                // The newest panes first, then the screens.
                repeat(pops) { if (!DetailPanes.closeNewest()) navController.popBackStack() }
            }
            index > depth -> {
                ownPops++
                window.history.go(depth - index)
            }
        }
    }
}
